import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input } from '@angular/core';
import { Search, Style } from '@local/client-contracts';
import { getIconByExtension } from '@local/ts-infra';
import { PopupService, UiIconModel } from '@local/ui-infra';
import { ContextMenuService } from '@shared/components';
import { EventsService, LogService } from '@shared/services';
import { ApplicationsService } from '@shared/services/applications.service';
import { DownloadsService } from '@shared/services/downloads.service';
import { FlagsService } from '@shared/services/flags.service';
import { KeyboardService } from '@shared/services/keyboard.service';
import { ResourcesService } from '@shared/services/resources.service';
import { RouterService } from '@shared/services/router.service';
import { getIconModel } from '@shared/utils/set-icon.util';
import { firstValueFrom, takeUntil } from 'rxjs';
import { WorkspacesService } from 'src/app/bar/services';
import { BlobsService } from 'src/app/bar/services/blobs/blob.service';
import { CommandBarService } from 'src/app/bar/services/command-bar.service';
import { CommandsService } from 'src/app/bar/services/commands/commands.service';
import { FavoritesService } from 'src/app/bar/services/favorites.service';
import { HubService } from 'src/app/bar/services/hub.service';
import { ResultsService } from 'src/app/bar/services/results.service';
import { ResourceViewItemComponent } from 'src/app/bar/views/results-views/view-item/resource-view-item.component';
import { GlobalErrorHandler } from 'src/app/global-error-handler';
import { FilePreviewService } from '../../preview/file-preview/services/file-preview.service';
import { PageType } from '../../results/models/view-filters';
import { ResultCommandBuilder } from '../../results/services/result-command-builder.service';
import { SearchPopUpService } from '../../special-views/search-popup/search-popup.service';
import { Constants } from '@local/common';

@Component({
  selector: 'files-view-item',
  templateUrl: './files-view-item.component.html',
  styleUrls: ['./files-view-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilesViewItemComponent extends ResourceViewItemComponent {
  @Input() set model(value: Search.ResultResourceItem) {
    super.model = value;
    this.getAppIcon();
    this.getResourceIcon();

    this.resultPreviewService.preview$.pipe(takeUntil(this.destroy$)).subscribe(async (preview) => {
      const command = await firstValueFrom(this.resultPreviewService.getContextMenuInvoke(this.model.id, preview));
      this.onContextMenuCommand(command.item, command.trigger, 'preview');
    });

    this.isRestricted = value?.link?.shareOptions?.restricted;
  }

  resourceIconModel: UiIconModel;
  appIconModel: UiIconModel;
  isRestricted: boolean;
  DISABLED_FAVORITES_WORKSPACE_FEATURE_FLAG = Constants.DISABLED_FAVORITES_WORKSPACE_FEATURE_FLAG;

  get model() {
    return super.model;
  }

  get name(): PageType {
    return 'files';
  }

  constructor(
    hostRef: ElementRef,
    protected cdr: ChangeDetectorRef,
    logService: LogService,
    protected applicationsService: ApplicationsService,
    contextMenuService: ContextMenuService,
    resultCommandBuilder: ResultCommandBuilder,
    hubService: HubService,
    eventsService: EventsService,
    downloadsService: DownloadsService,
    resourcesService: ResourcesService,
    commandsService: CommandsService,
    errorHandler: GlobalErrorHandler,
    commandBarService: CommandBarService,
    popupService: PopupService,
    keyboardService: KeyboardService,
    routerService: RouterService,
    private resultPreviewService: FilePreviewService,
    resultsService: ResultsService,
    flagsService: FlagsService,
    protected favoritesService: FavoritesService,
    protected blobsService: BlobsService,
    protected workspacesService: WorkspacesService,
    protected searchPopUpService: SearchPopUpService
  ) {
    super(
      hostRef,
      cdr,
      logService,
      applicationsService,
      contextMenuService,
      resultCommandBuilder,
      hubService,
      eventsService,
      downloadsService,
      resourcesService,
      commandsService,
      commandBarService,
      popupService,
      keyboardService,
      routerService,
      errorHandler,
      resultsService,
      flagsService,
      favoritesService,
      blobsService,
      workspacesService,
      searchPopUpService
    );
  }
  async onIconError(event: Event) {
    const appId = this.model.link.appId;
    const app = this.applicationsService.apps[appId];
    if (this.model.view) this.model.view.icon = app?.icon || this.fallbackIcon;
  }

  private async getAppIcon(): Promise<void> {
    const icon = this.model.view?.icon;
    if (!icon) {
      this.appIconModel = null;
      return;
    }

    if (['pc', 'gmail'].includes(this.model.resource.appId)) {
      const { icon } = await this.applicationsService.one(this.model.resource.appId);
      this.appIconModel = { type: 'img', value: icon };
      this.cdr.markForCheck();
      return;
    }
    this.appIconModel = getIconModel(icon);
    this.cdr.markForCheck();
  }

  private getResourceIcon(): void {
    const iconOverlay = this.model.view?.iconOverlay;
    if (this.model.resource.appId === 'pc' && !!iconOverlay) {
      this.resourceIconModel = getIconModel(iconOverlay);
      this.cdr.markForCheck();
      return;
    }

    const icon = getIconByExtension(this.model.resource?.traits?.name, this.model.resource?.appId);

    if (icon === '/assets/file-icons/general.svg' && this.model.view?.iconOverlay) {
      this.resourceIconModel = getIconModel(this.model.view?.iconOverlay as Style.Icon);
    } else {
      this.resourceIconModel = getIconModel({ lightUrl: icon });
    }
    this.cdr.markForCheck();
  }
}
